import { createBrowserHistory } from 'history'
import Cookies from "js-cookie";
import {Toast} from "antd-mobile";
import {CheckCircleFill, ExclamationCircleFill} from "antd-mobile-icons";
import React from "react";

const browserHistory = createBrowserHistory();

/**
 * param {Number} num 数值
 * param {Number} len 小数位的个数（几位小数）
 */
const setTokens = (data) => {
    localStorage.setItem('accessToken', data.accessToken);
};
const getTokens = () => {
    return localStorage.getItem('accessToken') || '';
};

const parseJwt = (token) => {
    try {
        const base64Url = token.split('.')[1];
        if (!base64Url) return null;
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.error('parseJwt error:', e);
        return null;
    }
};
// 从 accessToken 中同步取出 customerId，取不到返回 ''
const getCustomerId = () => {
    const token = getTokens();
    if (!token) return '';
    const payload = parseJwt(token);
    return payload?.customerId ?? '';
};
const getCookie = (cookieName) => {
    return Cookies.get(cookieName) || '';
};
const getAppToken = () => {
    return new Promise((resolve) => {
        let isResolved = false;
        if (window.getAccessToken) {
            const timeout = setTimeout(() => {
                if (!isResolved) {
                    isResolved = true;
                    console.log('getAccessToken timeout after 3 seconds, resolving with null');
                    localStorage.removeItem('accessToken');
                    resolve(null);
                }
            }, 1000); // 获取token超时报错
            window.getAccessToken((result) => {
                if (!isResolved) {
                    isResolved = true;
                    clearTimeout(timeout);
                    console.log('getAppToken', result);
                    setTokens({ accessToken: result });
                    resolve(result);
                }
            });
        } else {
            // 如果没有getAccessToken方法，立即resolve
            resolve(null);
        }
    });
};
const clearTokens = (data) => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
};
const getRootDomain = (hostname) => {
    const domainParts = hostname.split('.');

    if (domainParts.length <= 2) {
        return hostname;
    }

    return domainParts.slice(-2).join('.');
};
// 数字转为千分位显示并且保留两位小数
const formatNumber = (value) => {
    if (value == null || isNaN(value)) return '--';
    const number = parseFloat(value).toFixed(2);
    const parts = number.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); // 英文逗号
    return parts.join('.');
};

// 数字转为千分位显示保留整数
const formatIntegerWithCommas = (value) => {
    if (value == null || isNaN(value)) return '--';
    const number = Math.round(parseFloat(value)).toString(); // 四舍五入到整数并转为字符串
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ','); // 添加千分位逗号
};

// 红涨绿跌平灰
const formatColor = (value) => {
    if (value == null || isNaN(value)) return 'var(--color-text-primary)';
    if (value > 0) return 'var(--color-text-static-rise)';    // 绿色：正数
    if (value < 0) return 'var(--color-text-static-fall)';    // 红色：负数
    return 'var(--color-text-primary)';                 // 灰色：0
}
const mobileFormatColor = (value) => {
    if (value == null || isNaN(value)) return 'var(--color-text-primary)';
    if (value > 0) return 'var(--color-text-static-rise)';    // 红色：正数
    if (value < 0) return 'var(--color-text-static-fall)';    // 绿色：负数
    return 'var(--color-text-primary)';                 // 灰色：0
}
// 补正号
const fillAdd = (value) => {
    if (value == null || isNaN(value)) return '';
    if (value > 0) return '+';
    return '';
};

// 补%
const fillPercentageSign = (value) => {
    if (value == null || isNaN(value)) return '';
    return '%';
};

//  带后缀股票代码
const getMarketIdByRegex = (stockCode) => {
    const regexToMarketId = [
        { regex: /^(00)\d{4}\.SH$/, marketId: 0 },
        { regex: /^(60)\d{4}\.SH$/, marketId: 1 },
        { regex: /^(68)\d{4}\.SH/, marketId: 7 },
        { regex: /^(39)\d{4}\.SZ$/, marketId: 1000 },
        { regex: /^(00)\d{4}\.SZ$/, marketId: 1001 },
        { regex: /^(30)\d{4}\.SZ$/, marketId: 1008 },
    ];
    for (const item of regexToMarketId) {
        if (item.regex.test(stockCode)) {
            return item.marketId;
        }
    }
};
//  不带后缀股票代码
const getMarketIdByRegexNoSuffix = (stockCode) => {
    const regexToMarketId = [
        { regex: /^(60)\d{4}$/, marketId: 1 },
        { regex: /^(68)\d{4}$/, marketId: 7 },
        { regex: /^(00)\d{4}$/, marketId: 1001 },
        { regex: /^(30)\d{4}$/, marketId: 1008 },
    ];
    for (const item of regexToMarketId) {
        if (item.regex.test(stockCode)) {
            return item.marketId;
        }
    }
};

// 市值
const getMarketValue = (codeList, stockInfo) => {
    let marketValue = 0;
    codeList.forEach((item) => {
        marketValue += stockInfo[item.stockCode.split('.')[0]]?.now * item.num;
    });
    return marketValue || '--';
};
// 累计收益
const getTotalIncome = (codeList, income, stockInfo) => {
    if (codeList) {
        // a. 累计盈亏=总持仓盈亏+已平仓股票盈亏之和
        // b. 总持仓盈亏=多个持仓股票持仓盈亏之和，持仓取statu为:2-持仓中 、3-卖出中、5-部分卖出、7-卖出失败的股票
        // c. 单个股票持仓盈亏=股票当前价值+partialSellAmount -成本价-手续费
        // d. 股票当前价值=实时价格*（num -partialSellNum）
        //  e. 股票成本价=costPrice*num

        // 总持仓盈亏
        let holdOrderValue = 0;
        codeList.forEach((item) => {
            const costValue = item.costPrice * item.num;
            const newValue = stockInfo[item.stockCode.split('.')[0]]?.now * (item.num - item.partialSellNum);
            const itemIncome = newValue + item.partialSellAmount - costValue - item.fee;
            holdOrderValue += itemIncome;
        });

        // 单个股票持仓盈亏
        return holdOrderValue + income;
    } else {
        return 0.00;
    }
};
// 时间处理函数
const formatRelativeTime = (dateInput, locale = 'en-US', now = new Date()) => {
    const date = new Date(dateInput);

    const diffSeconds = Math.round((now - date) / 1000);
    const diffMinutes = Math.round(diffSeconds / 60);
    const diffHours = Math.round(diffMinutes / 60);

    // 获取“今天”和“昨天”的日历日起始时间，用于精确判断
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

    // 计算日历日差异
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((startOfToday - startOfDate) / (1000 * 60 * 60 * 24));

    // --- 中文 (zh-CN) 逻辑 ---
    if (locale === 'zh-CN') {
        if (diffSeconds < 60) {
            return `${Math.max(1, diffSeconds)}秒前`;
        }
        if (diffMinutes < 60) {
            return `${diffMinutes}分钟前`;
        }

        // **[修正] 优先检查是否是日历上的“昨天”**
        if (date < startOfToday && date >= startOfYesterday) {
            return '昨天';
        }

        // 如果不是“昨天”，再检查是否在24小时内（即“今天”的早些时候）
        if (diffHours < 24) {
            return `${diffHours}小时前`;
        }

        // 超过一天且不是昨天，按天数显示 (图片中格式为“X天”)
        if (diffDays <= 7) {
            return `${diffDays}天`;
        }
        if (date.getFullYear() === now.getFullYear()) {
            // 同年：X月X日
            return `${date.getMonth() + 1}月${date.getDate()}日`;
        } else {
            // 不同年：XX年X月X日
            const year = date.getFullYear().toString().slice(-2);
            return `${year}年${date.getMonth() + 1}月${date.getDate()}日`;
        }
    }

    // --- 英文 (en-US) 逻辑 ---
    if (diffSeconds < 60) {
        return 'Just now';
    }
    if (diffMinutes < 60) {
        return `${diffMinutes}m`;
    }

    // **[修正] 优先检查是否是日历上的“昨天”**
    if (date < startOfToday && date >= startOfYesterday) {
        return 'yesterday';
    }

    if (diffHours < 24) {
        return `${diffHours}h`;
    }

    if (diffDays <= 7) {
        return `${diffDays}d`;
    }
    if (date.getFullYear() === now.getFullYear()) {
        // 同年：Month Day
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
        // 不同年：Month Day, YY
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
    }
};


/**
 * 格式化日期时间为完整格式
 * 中文格式: YYYY年MM月DD日 HH:MM:SS
 * 英文格式: Month DD, YY HH:MM:SS
 * @param {string|Date} dateInput - 日期输入
 * @param {string} locale - 语言区域，默认 'en-US'
 * @returns {string} 格式化后的日期时间字符串
 */
const formatFullDateTime = (dateInput, locale = 'en-US') => {
    if (!dateInput) return '';

    const date = new Date(dateInput);

    // 检查日期是否有效
    if (isNaN(date.getTime())) return '';

    // 获取时间部分 HH:MM:SS
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const timeStr = `${hours}:${minutes}:${seconds}`;

    // 中文格式: YYYY年MM月DD日 HH:MM:SS
    if (locale === 'zh-CN') {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return `${year}年${month}月${day}日 ${timeStr}`;
    }

    // 英文格式: Month DD, YY HH:MM:SS
    else {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear().toString().slice(-2);
        return `${month} ${day}, ${year} ${timeStr}`;
    }
};
// 通用组件-错误toast
const ShowErrorToast = (content)=>{
    Toast.show({
        duration: 2000,
        position: 'bottom',
        getContainer: () => document.getElementById('root'),
        content: <div className={'toast-error-message'}><ExclamationCircleFill /><span className={'toast-error-message__text'}>{ content}</span></div>,
    })
}
// 通用组件-成功toast
const ShowSuccessToast = (content)=>{
    Toast.show({
        duration: 2000,
        position: 'bottom',
        getContainer: () => document.getElementById('root'),
        content: <div className={'toast-success-message'}><CheckCircleFill /><span className={'toast-success-message__text'}>{ content}</span></div>,
    })
}
// 通用组件-文字toast
const ShowPlainToast = (content)=>{
    Toast.show({
        duration: 2000,
        position: 'bottom',
        getContainer: () => document.getElementById('root'),
        content: <div className={'toast-plain-message'}><span className={'toast-plain-message__text'}>{ content}</span></div>,
    })
}
export default {
    browserHistory,
    setTokens,
    getTokens,
    parseJwt,
    getCustomerId,
    getCookie,
    clearTokens,
    getAppToken,
    getRootDomain,
    formatNumber,
    formatColor,
    mobileFormatColor,
    fillAdd,
    formatIntegerWithCommas,
    fillPercentageSign,
    getMarketIdByRegex,
    getMarketIdByRegexNoSuffix,
    getMarketValue,
    getTotalIncome,
    formatRelativeTime,
    formatFullDateTime,
    ShowErrorToast,
    ShowSuccessToast,
    ShowPlainToast
};
